import React, { useEffect, useRef, useState } from 'react';
import { CircleNotchIcon, PaintBrushIcon, EraserIcon, SparkleIcon } from '@phosphor-icons/react';
import { Modal } from '../ui/modal';
import { requestCodexText } from '../../lib/codexText';
import { acknowledgeClothingEdit, loadCanvasImage, loadClothingSource, pendingClothingMask, replaceClothing } from '../../lib/clothingEdit';
import { CLOTHING_MASK_PROMPT, REFERENCE_SHEET_CLOTHING_MASK_PROMPT, drawClothingRegions, growClothingMask, solidClothingMask, featherClothingMask, clothingMaskFeatherRadius, parseClothingRegions, type ClothingRegion } from '../../lib/clothingMask';

type Props = { source: string; target?: 'body' | 'reference-sheet'; onClose: () => void; onReplace: (blob: Blob) => Promise<void> };

export function ClothingEditDialog({ source, target = 'body', onClose, onReplace }: Props) {
    const maskPrompt = target === 'reference-sheet' ? REFERENCE_SHEET_CLOTHING_MASK_PROMPT : CLOTHING_MASK_PROMPT;
    const imageLabel = target === 'reference-sheet' ? 'reference sheet' : 'body image';
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const maskRef = useRef<HTMLCanvasElement | null>(null);
    const appliedMaskRef = useRef<HTMLCanvasElement | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const sourceRef = useRef('');
    const strokeRef = useRef<{ pointer: number; x: number; y: number } | null>(null);
    const [status, setStatus] = useState('Finding clothing areas…');
    const [error, setError] = useState<string | null>(null);
    const [ready, setReady] = useState(false);
    const [hasMask, setHasMask] = useState(false);
    const [resuming, setResuming] = useState(false);
    const [erase, setErase] = useState(false);
    const [brush, setBrush] = useState(24);
    const mounted = useRef(true);
    // Reuse initialization during React Strict Mode's effect replay to avoid duplicate paid requests.
    const initRef = useRef<Promise<{ source: string; image: HTMLImageElement; regions: ClothingRegion[]; warning: string | null; savedMask: HTMLImageElement | null }> | null>(null);

    const renderPreview = () => {
        const canvas = canvasRef.current, mask = maskRef.current, image = imageRef.current;
        if (!canvas || !mask || !image) return;
        const ctx = canvas.getContext('2d')!;
        const overlay = document.createElement('canvas');
        overlay.width = canvas.width; overlay.height = canvas.height;
        const overlayCtx = overlay.getContext('2d')!;
        const pixels = mask.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, mask.width, mask.height);
        // Saved in-flight masks must stay unchanged so a retry resumes its exact job.
        if (!resuming) {
            pixels.data.set(solidClothingMask(pixels.data));
            mask.getContext('2d', { willReadFrequently: true })!.putImageData(pixels, 0, 0);
            pixels.data.set(featherClothingMask(pixels.data, mask.width, mask.height, clothingMaskFeatherRadius(mask.width, mask.height)));
        }
        const appliedMask = appliedMaskRef.current || document.createElement('canvas');
        appliedMask.width = mask.width; appliedMask.height = mask.height;
        appliedMask.getContext('2d', { willReadFrequently: true })!.putImageData(pixels, 0, 0);
        appliedMaskRef.current = appliedMask;
        let selected = false;
        for (let i = 0; i < pixels.data.length; i += 4) {
            const intensity = pixels.data[i];
            if (intensity) selected = true;
            pixels.data[i] = 192; pixels.data[i + 1] = 132; pixels.data[i + 2] = 252;
            pixels.data[i + 3] = Math.round(intensity * 0.55);
        }
        overlayCtx.putImageData(pixels, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0);
        ctx.drawImage(overlay, 0, 0);
        setHasMask(selected);
    };

    useEffect(() => {
        mounted.current = true;
        let active = true;
        if (!initRef.current) initRef.current = (async () => {
            const data = await loadClothingSource(source);
            const image = await loadCanvasImage(data);
            if (image.naturalWidth * image.naturalHeight > 12_000_000) throw new Error('Use an image under 12 megapixels for clothing editing.');
            let regions: ClothingRegion[] = [], warning: string | null = null;
            const pending = await pendingClothingMask(data);
            if (pending) return { source: data, image, regions, warning: 'An edit is already saved for this image. Apply to resume it using this selection.', savedMask: await loadCanvasImage(pending) };
            try {
                regions = parseClothingRegions(await requestCodexText(maskPrompt, [data]));
                if (!regions.length) warning = 'No underwear detected. Paint the areas you want to replace.';
            } catch (err) { warning = err instanceof Error ? err.message : 'Detection failed. Paint the clothing areas manually.'; }
            return { source: data, image, regions, warning, savedMask: null };
        })();
        initRef.current.then(result => {
            if (!active || !canvasRef.current) return;
            sourceRef.current = result.source; imageRef.current = result.image;
            const mask = document.createElement('canvas');
            mask.width = result.image.naturalWidth; mask.height = result.image.naturalHeight;
            maskRef.current = mask;
            canvasRef.current.width = mask.width; canvasRef.current.height = mask.height;
            drawClothingRegions(mask.getContext('2d', { willReadFrequently: true })!, result.regions, mask.width, mask.height);
            if (result.savedMask) mask.getContext('2d', { willReadFrequently: true })!.drawImage(result.savedMask, 0, 0);
            setResuming(!!result.savedMask);
            setError(result.warning); setReady(true); setStatus('');
            // Preview on the next render, after the saved-mask flag takes effect.
        }).catch(err => { if (active) { setError(err instanceof Error ? err.message : 'Could not load body image.'); setStatus(''); } });
        return () => { active = false; mounted.current = false; };
    }, [source]);

    useEffect(() => { if (ready) renderPreview(); }, [ready, resuming]);

    const growEdges = () => {
        if (!ready || status || resuming || !maskRef.current) return;
        const mask = maskRef.current, ctx = mask.getContext('2d', { willReadFrequently: true })!;
        const pixels = ctx.getImageData(0, 0, mask.width, mask.height);
        pixels.data.set(growClothingMask(pixels.data, mask.width, mask.height, Math.max(2, Math.min(8, Math.round(Math.min(mask.width, mask.height) / 200)))));
        ctx.putImageData(pixels, 0, 0);
        renderPreview();
    };

    const detect = async () => {
        if (status || !ready || resuming) return;
        setStatus('Finding clothing areas…'); setError(null);
        try {
            const regions = parseClothingRegions(await requestCodexText(maskPrompt, [sourceRef.current]));
            if (!mounted.current || !maskRef.current) return;
            const mask = maskRef.current;
            drawClothingRegions(mask.getContext('2d', { willReadFrequently: true })!, regions, mask.width, mask.height);
            renderPreview();
            if (!regions.length) setError('No underwear detected. Paint the areas you want to replace.');
        } catch (err) { if (mounted.current) setError(err instanceof Error ? err.message : 'Could not detect clothing.'); }
        finally { if (mounted.current) setStatus(''); }
    };

    const paint = (event: React.PointerEvent<HTMLCanvasElement>, start = false) => {
        const canvas = canvasRef.current, mask = maskRef.current;
        if (!ready || status || resuming || !canvas || !mask) return;
        const rect = canvas.getBoundingClientRect();
        const x = Math.max(0, Math.min(mask.width, (event.clientX - rect.left) / rect.width * mask.width));
        const y = Math.max(0, Math.min(mask.height, (event.clientY - rect.top) / rect.height * mask.height));
        if (start) {
            if (event.button !== 0) return;
            canvas.setPointerCapture(event.pointerId);
            strokeRef.current = { pointer: event.pointerId, x, y };
        }
        const last = strokeRef.current;
        if (!last || last.pointer !== event.pointerId) return;
        const ctx = mask.getContext('2d', { willReadFrequently: true })!;
        ctx.strokeStyle = ctx.fillStyle = erase ? 'black' : 'white';
        // Brush size is expressed in preview pixels, independent of source resolution.
        ctx.lineWidth = brush * mask.width / rect.width;
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(x, y); ctx.stroke();
        ctx.beginPath(); ctx.arc(x, y, ctx.lineWidth / 2, 0, Math.PI * 2); ctx.fill();
        strokeRef.current = { pointer: event.pointerId, x, y };
        renderPreview();
    };

    const apply = async () => {
        if (!hasMask || !appliedMaskRef.current || status) return;
        setStatus('Changing clothing…'); setError(null);
        try {
            const blob = await replaceClothing(sourceRef.current, appliedMaskRef.current, message => { if (mounted.current) setStatus(message); });
            if (!mounted.current) return;
            setStatus(`Saving ${imageLabel}…`);
            await onReplace(blob);
            await acknowledgeClothingEdit(sourceRef.current);
            if (mounted.current) onClose();
        } catch (err) { if (mounted.current) setError(err instanceof Error ? err.message : 'Could not change clothing.'); }
        finally { if (mounted.current) setStatus(''); }
    };

    const close = () => { if (!status || !ready && !imageRef.current) onClose(); };
    const button = 'px-3 py-2 rounded-lg border border-iron text-sm text-zinc-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed';
    return (
        <Modal isOpen onClose={close} title="Remove Clothes" description="Review the purple selection before creating the Unclothed version." size="xl" showCloseButton={!status}>
            <div className="p-4 space-y-4">
                <div className="flex justify-center rounded-lg bg-onyx overflow-hidden min-h-32">
                    <canvas ref={canvasRef} aria-label="Clothing selection; drag to paint or erase" className={`max-w-full max-h-[43vh] touch-none ${ready ? 'cursor-crosshair' : 'hidden'}`} style={{ width: 'auto', height: 'auto' }}
                        onPointerDown={event => paint(event, true)} onPointerMove={event => paint(event)}
                        onPointerUp={() => { strokeRef.current = null; }} onPointerCancel={() => { strokeRef.current = null; }} />
                    {!ready && <div className="p-10 text-zinc-400 text-sm">{status || `${imageLabel} unavailable`}</div>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button className={`${button} ${!erase ? 'bg-purple-500/15 border-purple-400/40' : ''}`} disabled={!ready || !!status || resuming} onClick={() => setErase(false)} aria-pressed={!erase}><PaintBrushIcon className="inline mr-1" />Paint</button>
                    <button className={`${button} ${erase ? 'bg-purple-500/15 border-purple-400/40' : ''}`} disabled={!ready || !!status || resuming} onClick={() => setErase(true)} aria-pressed={erase}><EraserIcon className="inline mr-1" />Erase</button>
                    <label className="flex items-center gap-2 text-xs text-zinc-400">Brush<input aria-label="Brush size" type="range" min="4" max="64" value={brush} disabled={!ready || !!status || resuming} onChange={event => setBrush(Number(event.target.value))} className="w-20 accent-purple-400" /></label>
                    <button className={button} disabled={!ready || !!status || resuming} onClick={() => { const mask = maskRef.current!; drawClothingRegions(mask.getContext('2d', { willReadFrequently: true })!, [], mask.width, mask.height); renderPreview(); }}>Clear</button>
                    <button className={button} disabled={!ready || !hasMask || !!status || resuming} onClick={growEdges}>Grow edges</button>
                    <button className={button} disabled={!ready || !!status || resuming} onClick={detect}>Detect again · 1 credit</button>
                </div>
                <p className="text-xs text-zinc-400">Cover every garment edge, strap and tie with purple. Grow edges to include missed trim, then erase any unwanted areas. The outer edge fades gently into the original image. Only selected areas will change. Detection uses 1 credit; applying uses 1 credit.</p>
                {error && <p role="alert" className="text-sm text-amber-300">{error}</p>}
                {status && <p role="status" className="flex items-center gap-2 text-sm text-purple-200"><CircleNotchIcon className="animate-spin" />{status}</p>}
                <div className="flex justify-end gap-2">
                    <button className={button} disabled={!!status} onClick={onClose}>Cancel</button>
                    <button className={`${button} bg-purple-500/20 border-purple-400/40`} disabled={!ready || !hasMask || !!status} onClick={apply}><SparkleIcon className="inline mr-1" />{resuming ? 'Resume Unclothed' : 'Create Unclothed · 1 credit'}</button>
                </div>
            </div>
        </Modal>
    );
}
